import { useTranslation } from 'react-i18next';
import { AlertTriangle, Power, ShieldAlert, Wallet, type LucideIcon } from 'lucide-react';
import { Skeleton, TruncatedAmount } from '@shared/ui';
import type { WalletMonitoringSummary } from '@shared/api/contracts';
import { formatWalletMoney } from '@entities/wallets';

interface WalletsSummaryCardsProps {
  summary?: WalletMonitoringSummary;
  isLoading?: boolean;
}

const COUNT_ITEMS: Array<{
  key: 'totalWallets' | 'totalAddresses' | 'activeAddresses' | 'highRiskAddresses';
  labelKey: string;
  icon: LucideIcon;
  iconClassName: string;
}> = [
  {
    key: 'totalWallets',
    labelKey: 'wallets.summary.totalWallets',
    icon: Wallet,
    iconClassName: 'text-primary',
  },
  {
    key: 'totalAddresses',
    labelKey: 'wallets.summary.totalAddresses',
    icon: Wallet,
    iconClassName: 'text-primary',
  },
  {
    key: 'activeAddresses',
    labelKey: 'wallets.summary.activeAddresses',
    icon: Power,
    iconClassName: 'text-emerald-600',
  },
  {
    key: 'highRiskAddresses',
    labelKey: 'wallets.summary.highRisk',
    icon: ShieldAlert,
    iconClassName: 'text-red-600',
  },
];

export function WalletsSummaryCards({ summary, isLoading }: WalletsSummaryCardsProps) {
  const { t } = useTranslation('cabinet');

  if (isLoading && !summary) {
    return (
      <div className="grid grid-cols-2 gap-2 sm:gap-3.5 md:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-16 rounded-lg sm:h-20 sm:rounded-xl" />
        ))}
      </div>
    );
  }

  if (!summary) return null;

  const planValue = formatWalletMoney(summary.planAmount, { asset: summary.planAsset });
  const factValue = formatWalletMoney(summary.factAmount, { asset: summary.factAsset });

  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2 sm:gap-3.5 md:grid-cols-3 lg:grid-cols-6">
        {COUNT_ITEMS.map((item) => {
          const Icon = item.icon;
          const value = String(summary[item.key]);
          return (
            <div key={item.key} className="stat-card min-w-[140px] sm:min-w-[156px]">
              <div className="mb-1.5 flex items-center gap-1.5">
                <Icon className={`h-3.5 w-3.5 shrink-0 ${item.iconClassName}`} />
                <span className="stat-card-label truncate">{t(item.labelKey)}</span>
              </div>
              <div className="stat-card-value truncate tabular-nums">{value}</div>
            </div>
          );
        })}
        <div className="stat-card min-w-[140px] sm:min-w-[156px]">
          <div className="mb-1.5 flex items-center gap-1.5">
            <Wallet className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="stat-card-label truncate">{t('wallets.summary.planTotal')}</span>
          </div>
          <TruncatedAmount value={planValue} className="stat-card-value" />
        </div>
        <div className="stat-card min-w-[140px] sm:min-w-[156px]">
          <div className="mb-1.5 flex items-center gap-1.5">
            <Wallet className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="stat-card-label truncate">{t('wallets.summary.factTotal')}</span>
          </div>
          <TruncatedAmount value={factValue} className="stat-card-value" />
        </div>
      </div>

      {(summary.staleBalances > 0 || summary.unvaluedBalances > 0) && (
        <div className="flex flex-wrap gap-2">
          {summary.staleBalances > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-yellow-500/15 px-2.5 py-1 text-xs font-medium text-yellow-800">
              <AlertTriangle className="h-3.5 w-3.5" />
              {t('wallets.summary.staleBalances', { count: summary.staleBalances })}
            </span>
          )}
          {summary.unvaluedBalances > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/15 px-2.5 py-1 text-xs font-medium text-orange-800">
              <AlertTriangle className="h-3.5 w-3.5" />
              {t('wallets.summary.unvaluedBalances')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
